import { useState, useEffect, useRef, useCallback } from 'react';

const DEFAULT_DURATION = 60; // seconds

/**
 * 基于时间戳的验证码倒计时 hook。
 *
 * 核心改进：存储「截止时间戳」而非「每秒递减的计数」。
 * - 不受 setInterval 漂移影响
 * - 用户关闭/切换 tab 后重新打开，剩余时间仍与真实流逝时间一致
 *
 * @param {string} storageKey - localStorage 中存储截止时间戳的 key
 * @param {number} duration - 倒计时总时长，默认 60 秒
 */
export function useCountdown(storageKey, duration = DEFAULT_DURATION) {
  const [remaining, setRemaining] = useState(duration); // UI 显示的剩余秒数
  const timerRef = useRef(null);
  const expiresAtRef = useRef(null); // 截止时间戳，毫秒

  /** 根据当前时间计算剩余秒数 */
  const computeRemaining = useCallback(() => {
    if (expiresAtRef.current == null) return 0;
    return Math.max(0, Math.ceil((expiresAtRef.current - Date.now()) / 1000));
  }, []);

  /** 启动倒计时：写入截止时间戳，开始 tick */
  const start = useCallback(() => {
    // 清除旧 timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const expiresAt = Date.now() + duration * 1000;
    expiresAtRef.current = expiresAt;

    // 持久化截止时间戳（不是计数）
    localStorage.setItem(storageKey, expiresAt);

    // 立即更新 UI
    setRemaining(duration);

    // 每秒 tick：基于时间戳计算，不依赖计数递减
    timerRef.current = setInterval(() => {
      const r = computeRemaining();
      if (r <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        expiresAtRef.current = null;
        localStorage.removeItem(storageKey);
        setRemaining(duration);
      } else {
        setRemaining(r);
      }
    }, 1000);
  }, [duration, storageKey, computeRemaining]);

  /** 恢复倒计时：从 localStorage 读取截止时间戳并继续 */
  const resume = useCallback(() => {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return;

    const expiresAt = Number(stored);
    expiresAtRef.current = expiresAt;

    const r = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
    if (r <= 0) {
      localStorage.removeItem(storageKey);
      setRemaining(duration);
      return;
    }

    setRemaining(r);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      const rr = computeRemaining();
      if (rr <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        expiresAtRef.current = null;
        localStorage.removeItem(storageKey);
        setRemaining(duration);
      } else {
        setRemaining(rr);
      }
    }, 1000);
  }, [storageKey, duration, computeRemaining]);

  /** 组件挂载时自动恢复 */
  useEffect(() => {
    resume();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [resume]);

  return { remaining, start };
}
